import React, { useState, useEffect, useRef } from 'react'
import { Volume2, Download, Loader2, AlertCircle, Plus, GripVertical, Play, Pause, Search, Library, BookmarkPlus, Check } from 'lucide-react'
import { sfxApi, libraryApi } from '../api/client'

interface SfxHistoryItem {
  job_id: string
  text: string
  seconds: number
  status: 'pending' | 'ready' | 'failed'
  download_url?: string
  error?: string
  duration?: number
  created: Date
}

interface SfxLibItem {
  slug: string
  name: string
  category: string
  tags: string[]
  seconds: number
  job_id: string
}

interface SfxPanelProps {
  /** Place a finished SFX on the timeline's SFX lane. */
  onAddToTimeline?: (clip: { url: string; text: string; duration?: number }) => void
}

const POLL_INTERVAL_MS = 3000
const MAX_TEXT_LENGTH = 500
const HISTORY_KEY = 'legit2clip_sfx_history'
const PRESETS = ['whoosh transition', 'deep boom impact', 'riser build-up', 'cinematic hit', 'notification ding', 'applause', 'camera shutter', 'glitch', 'swoosh', 'page turn']

export function SfxPanel({ onAddToTimeline }: SfxPanelProps = {}) {
  const [view, setView] = useState<'library' | 'generate'>('library')
  // Which generated sounds have been kept, so the button can say so rather than
  // inviting a duplicate save the user cannot tell apart afterwards.
  const [kept, setKept] = useState<Record<string, 'saving' | 'saved'>>({})

  const keepInLibrary = async (jobId: string, name: string) => {
    setKept((k) => ({ ...k, [jobId]: 'saving' }))
    try {
      await libraryApi.saveFromJob(jobId, 'sfx', name || 'Generated sound')
      setKept((k) => ({ ...k, [jobId]: 'saved' }))
    } catch {
      setKept((k) => { const n = { ...k }; delete n[jobId]; return n })
    }
  }

  // ── generate state ──
  const [text, setText] = useState('')
  const [seconds, setSeconds] = useState(5)
  const [isGenerating, setIsGenerating] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [statusText, setStatusText] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [history, setHistory] = useState<SfxHistoryItem[]>([])
  const [loaded, setLoaded] = useState(false)

  // ── library state ──
  const [lib, setLib] = useState<SfxLibItem[]>([])
  const [libCats, setLibCats] = useState<string[]>([])
  const [libCat, setLibCat] = useState('')
  const [libQ, setLibQ] = useState('')
  const [libLoading, setLibLoading] = useState(false)
  const [libLoaded, setLibLoaded] = useState(false)
  const [libError, setLibError] = useState('')
  const [playing, setPlaying] = useState<string | null>(null)
  const auditionRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY)
      if (raw) {
        const parsed: SfxHistoryItem[] = JSON.parse(raw).map((h: any) => ({ ...h, created: new Date(h.created) }))
        setHistory(parsed)
        const pend = parsed.find(h => h.status === 'pending')
        if (pend) setActiveJobId(pend.job_id)
      }
    } catch {}
    setLoaded(true)
  }, [])
  useEffect(() => { if (loaded) { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(history)) } catch {} } }, [history, loaded])

  // Load the curated SFX library once, the first time the Library tab is shown.
  useEffect(() => {
    if (view !== 'library' || libLoaded || libLoading) return
    setLibLoading(true); setLibError('')
    sfxApi.library()
      .then(d => { setLib(d.sfx || []); setLibCats(d.categories || []); setLibLoaded(true) })
      .catch((e: any) => setLibError(e?.response?.data?.error || 'Could not load the SFX library'))
      .finally(() => setLibLoading(false))
  }, [view, libLoaded, libLoading])

  useEffect(() => {
    if (!activeJobId) return
    let cancelled = false
    setIsGenerating(true)
    const poll = async () => {
      try {
        const data = await sfxApi.getStatus(activeJobId)
        if (cancelled) return
        if (data.ready) {
          const url = sfxApi.getDownloadUrl(activeJobId)
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'ready', download_url: url } : item))
          setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          const err = data.error_message || 'Generation failed'
          setHistory(prev => prev.map(item => item.job_id === activeJobId ? { ...item, status: 'failed', error: err } : item))
          setErrorMessage(err); setIsGenerating(false); setActiveJobId(null); setStatusText('')
        } else {
          const pct = typeof data.progress === 'number' ? ` — ${Math.round(data.progress)}%` : ''
          setStatusText(`${data.status || 'processing'}${pct}`)
        }
      } catch {}
    }
    poll()
    const interval = window.setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancelled = true; window.clearInterval(interval) }
  }, [activeJobId])

  const handleGenerate = async () => {
    const prompt = text.trim()
    if (!prompt || isGenerating) return
    setErrorMessage(''); setIsGenerating(true); setStatusText('Submitting...')
    try {
      const data = await sfxApi.generate({ text: prompt, seconds })
      if (!data.job_id) throw new Error('No job id returned')
      setHistory(prev => [{ job_id: data.job_id, text: prompt, seconds, status: 'pending' as const, created: new Date() }, ...prev])
      setActiveJobId(data.job_id); setStatusText('Queued...')
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error || error?.message || 'Failed to start generation'); setIsGenerating(false); setStatusText('')
    }
  }

  const setDuration = (jobId: string, d: number) => {
    if (!d || !isFinite(d)) return
    setHistory(prev => prev.map(item => item.job_id === jobId ? { ...item, duration: d } : item))
  }

  const audition = (s: SfxLibItem) => {
    const el = auditionRef.current
    if (!el) return
    if (playing === s.slug) { el.pause(); setPlaying(null); return }
    el.src = sfxApi.getDownloadUrl(s.job_id)
    el.play().then(() => setPlaying(s.slug)).catch(() => setPlaying(null))
  }

  const addLibToTimeline = (s: SfxLibItem) =>
    onAddToTimeline?.({ url: sfxApi.getDownloadUrl(s.job_id), text: s.name, duration: s.seconds })

  const libFiltered = lib.filter(s =>
    (!libCat || s.category === libCat) &&
    (!libQ.trim() ||
      s.name.toLowerCase().includes(libQ.toLowerCase()) ||
      s.tags.some(t => t.includes(libQ.toLowerCase()))))

  const TabBtn = ({ id, label }: { id: 'library' | 'generate'; label: string }) => (
    <button onClick={() => setView(id)}
      className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${view === id ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}>{label}</button>
  )

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 flex-1 min-h-0 flex flex-col">
      <audio ref={auditionRef} onEnded={() => setPlaying(null)} className="hidden" />
      <div className="p-3 border-b border-gray-600 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Volume2 className="text-gray-400" size={16} />
          <h3 className="text-sm font-semibold text-white">Sound Effects</h3>
        </div>
        <div className="flex gap-1 bg-gray-900/60 rounded-lg p-1">
          <TabBtn id="library" label="Library" />
          <TabBtn id="generate" label="Generate" />
        </div>

        {view === 'generate' && (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={MAX_TEXT_LENGTH}
              placeholder="Describe the sound... (e.g. 'whoosh transition', 'crowd cheering', 'rain on a tin roof')" disabled={isGenerating}
              className="w-full mt-2 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white resize-none placeholder-gray-500 focus:outline-none focus:border-indigo-500 disabled:opacity-50" />
            <div className="flex flex-wrap gap-1 mt-1.5">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => setText(p)} disabled={isGenerating}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 transition-colors disabled:opacity-40">{p}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-gray-400 shrink-0">Length</span>
              <input type="range" min={1} max={15} step={1} value={seconds} onChange={(e) => setSeconds(Number(e.target.value))} disabled={isGenerating} className="flex-1 accent-indigo-500" />
              <span className="text-xs text-gray-300 font-mono w-8 text-right shrink-0">{seconds}s</span>
            </div>
            <button onClick={handleGenerate} disabled={!text.trim() || isGenerating}
              className="w-full mt-2 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {isGenerating ? (<><Loader2 className="animate-spin" size={14} />{statusText || 'Generating...'}</>) : '🔊 Generate SFX'}
            </button>
            {errorMessage && (
              <div className="flex items-start gap-1.5 mt-2 bg-red-900/40 border border-red-600/40 rounded-lg px-3 py-2">
                <AlertCircle className="text-red-400 shrink-0 mt-0.5" size={12} />
                <p className="text-xs text-red-200">{errorMessage}</p>
              </div>
            )}
          </>
        )}

        {view === 'library' && (
          <>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" size={13} />
              <input value={libQ} onChange={(e) => setLibQ(e.target.value)} placeholder="Search 100 production SFX…"
                className="w-full bg-gray-900 border border-gray-600 rounded-lg pl-7 pr-3 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              <button onClick={() => setLibCat('')}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${!libCat ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-600 text-gray-400 hover:text-white'}`}>all</button>
              {libCats.map((c) => (
                <button key={c} onClick={() => setLibCat(c)}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${libCat === c ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-600 text-gray-400 hover:text-white'}`}>{c}</button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── body ── */}
      {view === 'generate' ? (
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Volume2 className="mx-auto mb-2" size={24} />
              <p className="text-xs">Describe a sound effect or tap a preset, then generate.</p>
              <p className="text-[11px] mt-1 text-gray-600">Generated SFX stay here for this session.</p>
            </div>
          ) : (
            history.map((item) => (
              <div key={item.job_id} className="bg-gray-900/60 rounded-lg p-2 border border-gray-600/40">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-white truncate flex-1">{item.text}</p>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] text-gray-500 font-mono">{item.seconds}s</span>
                    {item.status === 'pending' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-300 flex items-center gap-1"><Loader2 className="animate-spin" size={9} />generating</span>)}
                    {item.status === 'ready' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/30 text-green-300">ready</span>)}
                    {item.status === 'failed' && (<span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/30 text-red-300">failed</span>)}
                  </div>
                </div>
                {item.status === 'ready' && item.download_url && (
                  <>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <audio controls preload="metadata" src={item.download_url} onLoadedMetadata={(e) => setDuration(item.job_id, (e.target as HTMLAudioElement).duration)} className="flex-1 h-8 min-w-0" />
                      <a href={item.download_url} download={`sfx-${item.job_id.slice(0, 8)}.wav`} className="p-1 text-gray-400 hover:text-white shrink-0" title="Download sfx"><Download size={13} /></a>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button onClick={() => item.download_url && onAddToTimeline?.({ url: item.download_url, text: item.text, duration: item.duration || item.seconds })}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-sky-600/80 hover:bg-sky-600 text-white text-[11px] font-medium"><Plus size={11} /> Add to SFX track</button>
                      <span draggable
                        onDragStart={(e) => { e.dataTransfer.setData('application/x-sfx-clip', JSON.stringify({ url: item.download_url, text: item.text, duration: item.duration || item.seconds })); e.dataTransfer.effectAllowed = 'copy' }}
                        title="Drag onto the SFX track"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-600 text-gray-400 hover:text-white text-[11px] cursor-grab active:cursor-grabbing select-none"><GripVertical size={11} /> drag</span>
                      {/* Keep it. A generated sound otherwise lives on a 7-day
                          retention and disappears; saving copies the bytes into the
                          user's own library so the assistant can still find it. */}
                      <button
                        onClick={() => keepInLibrary(item.job_id, item.text)}
                        disabled={!!kept[item.job_id]}
                        title={kept[item.job_id] === 'saved' ? 'In your library' : 'Keep in my library'}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-600 text-gray-400 hover:text-white text-[11px] disabled:opacity-60">
                        {kept[item.job_id] === 'saving'
                          ? <Loader2 size={11} className="animate-spin" />
                          : kept[item.job_id] === 'saved' ? <Check size={11} /> : <BookmarkPlus size={11} />}
                        {kept[item.job_id] === 'saved' ? 'kept' : 'keep'}
                      </button>
                    </div>
                  </>
                )}
                {item.status === 'failed' && item.error && (<p className="text-[11px] text-red-300 mt-1">{item.error}</p>)}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {libLoading && (<div className="text-center py-8 text-gray-500"><Loader2 className="animate-spin mx-auto mb-2" size={20} /><p className="text-xs">Loading SFX library…</p></div>)}
          {libError && (<div className="flex items-start gap-1.5 bg-red-900/40 border border-red-600/40 rounded-lg px-3 py-2"><AlertCircle className="text-red-400 shrink-0 mt-0.5" size={12} /><p className="text-xs text-red-200">{libError}</p></div>)}
          {!libLoading && !libError && libFiltered.length === 0 && libLoaded && (
            <div className="text-center py-8 text-gray-500"><Library className="mx-auto mb-2" size={24} /><p className="text-xs">No SFX match your search.</p></div>
          )}
          {libFiltered.map((s) => (
            <div key={s.slug} className="group flex items-center gap-2 bg-gray-900/60 rounded-lg px-2 py-1.5 border border-gray-600/40 hover:border-gray-500/60">
              <button onClick={() => audition(s)} title="Audition"
                className="shrink-0 w-7 h-7 rounded-full bg-indigo-600/80 hover:bg-indigo-600 text-white flex items-center justify-center">
                {playing === s.slug ? <Pause size={13} /> : <Play size={13} />}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white truncate">{s.name}</p>
                <p className="text-[10px] text-gray-500 truncate">{s.category} · {s.seconds}s</p>
              </div>
              <button onClick={() => addLibToTimeline(s)} title="Add to SFX track"
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-600/80 hover:bg-sky-600 text-white text-[11px] font-medium"><Plus size={11} /> Add</button>
              <span draggable
                onDragStart={(e) => { e.dataTransfer.setData('application/x-sfx-clip', JSON.stringify({ url: sfxApi.getDownloadUrl(s.job_id), text: s.name, duration: s.seconds })); e.dataTransfer.effectAllowed = 'copy' }}
                title="Drag onto the SFX track"
                className="shrink-0 text-gray-500 hover:text-white cursor-grab active:cursor-grabbing select-none opacity-0 group-hover:opacity-100"><GripVertical size={13} /></span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
